import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { formatGerman } from '../../helpers/chartUtils';
import { tokens } from '../../theme';
import LeadwallTimelineChart, { LeadwallTimelinePoint } from './LeadwallTimelineChart';
import LeadwallOverallChart, { DailyImpression } from './LeadwallOverallChart';

interface FunnelRow {
  variant: string;
  soi: number;
  doi: number;
  doi_pct: number | null;
}

interface ZTestResult {
  p1: number;
  p2: number;
  diff: number;
  z: number | null;
  pValue: number | null;
  ci95Low: number | null;
  ci95High: number | null;
  nRequiredPerArm: number | null;
  significant: boolean;
}

interface FunnelSummary {
  rows: FunnelRow[];
  zTest: ZTestResult | null;
  hasA1: boolean;
  hasA2: boolean;
}

interface Ga4VariantRow {
  variant: string;
  impressions: number;
  submitted_ga4: number;
  errors_ga4: number;
}

interface Ga4Summary {
  rows: Ga4VariantRow[];
  zTestDoiPerImpression: ZTestResult | null;
}

interface ArticleCountRow {
  article_count: number;
  variant: string;
  impressions: number;
}

interface VariantCopy {
  variant: string;
  headline: string;
  intro: string;
  benefits_header: string;
  benefits: string[];
  button: string;
  trust: string;
  updated_at: string;
}

interface TopArticle {
  article_id: number;
  post_title: string;
  permalink: string | null;
  variant_a1_soi: number;
  variant_a2_soi: number;
  variant_a1_doi: number;
  variant_a2_doi: number;
  soi: number;
  doi: number;
  doi_pct: number | null;
}

interface LpsCrossCheck {
  variant: string;
  rows_total: number;
  doi_confirmed: number;
  unconfirmed: number;
  errored: number;
}

const SAMPLE_SIZE_THRESHOLD = 400;
const PLACEHOLDER_SAMPLE = 'Beispielwerk AG';

const formatPct = (value: number | null, digits = 1): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} %`;
};

const formatPp = (value: number | null, digits = 2): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const pp = value * 100;
  const sign = pp > 0 ? '+' : '';
  return `${sign}${pp.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} pp`;
};

const formatPValue = (p: number | null): string => {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  if (p < 0.001) return 'p < 0,001';
  return `p = ${p.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

const articleUrl = (row: TopArticle) =>
  row.permalink ?? `https://www.finanztrends.de/?p=${row.article_id}`;

const LeadwallEvaluationPage: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [summary, setSummary] = useState<FunnelSummary | null>(null);
  const [ga4Summary, setGa4Summary] = useState<Ga4Summary | null>(null);
  const [articleCount, setArticleCount] = useState<ArticleCountRow[]>([]);
  const [dailyImpressions, setDailyImpressions] = useState<DailyImpression[]>([]);
  const [timeline, setTimeline] = useState<LeadwallTimelinePoint[]>([]);
  const [topArticles, setTopArticles] = useState<TopArticle[]>([]);
  const [variantCopy, setVariantCopy] = useState<VariantCopy[]>([]);
  const [lpsCrossCheck, setLpsCrossCheck] = useState<LpsCrossCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [substituteAktie, setSubstituteAktie] = useState(false);

  const queryParams = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return null;
    return {
      startDate: dayjs(filterState.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(filterState.endDate).format('YYYY-MM-DD'),
    };
  }, [filterState.startDate, filterState.endDate]);

  useEffect(() => {
    if (!queryParams) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [
          summaryResp,
          ga4Resp,
          dailyImpResp,
          articleCountResp,
          timelineResp,
          topResp,
          copyResp,
          lpsResp,
        ] = await Promise.all([
          fetchData<FunnelSummary>(
            '/api/leadwall/summary',
            queryParams,
            false,
          ) as Promise<FunnelSummary>,
          fetchData<Ga4Summary>(
            '/api/leadwall/ga4-summary',
            queryParams,
            false,
          ) as Promise<Ga4Summary>,
          fetchData<DailyImpression>('/api/leadwall/ga4-daily-impressions', queryParams) as Promise<
            DailyImpression[]
          >,
          fetchData<ArticleCountRow>(
            '/api/leadwall/article-count-breakdown',
            queryParams,
          ) as Promise<ArticleCountRow[]>,
          fetchData<LeadwallTimelinePoint>('/api/leadwall/timeline', queryParams) as Promise<
            LeadwallTimelinePoint[]
          >,
          fetchData<TopArticle>('/api/leadwall/top-articles', queryParams) as Promise<TopArticle[]>,
          fetchData<VariantCopy>('/api/leadwall/variant-copy', {}) as Promise<VariantCopy[]>,
          fetchData<LpsCrossCheck>('/api/leadwall/lps-cross-check', queryParams) as Promise<
            LpsCrossCheck[]
          >,
        ]);
        if (cancelled) return;
        setSummary(summaryResp);
        setGa4Summary(ga4Resp);
        setDailyImpressions(Array.isArray(dailyImpResp) ? dailyImpResp : []);
        setArticleCount(Array.isArray(articleCountResp) ? articleCountResp : []);
        setTimeline(Array.isArray(timelineResp) ? timelineResp : []);
        setTopArticles(Array.isArray(topResp) ? topResp : []);
        setVariantCopy(Array.isArray(copyResp) ? copyResp : []);
        setLpsCrossCheck(Array.isArray(lpsResp) ? lpsResp : []);
      } catch (err) {
        console.error('Error loading leadwall evaluation data:', err);
        if (!cancelled) {
          setSummary(null);
          setGa4Summary(null);
          setDailyImpressions([]);
          setArticleCount([]);
          setTimeline([]);
          setTopArticles([]);
          setVariantCopy([]);
          setLpsCrossCheck([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const a1Row = summary?.rows.find((r) => r.variant === 'A1');
  const a2Row = summary?.rows.find((r) => r.variant === 'A2');

  const minDoiPerArm = a1Row && a2Row ? Math.min(a1Row.doi, a2Row.doi) : 0;
  const sampleSizeOk = minDoiPerArm >= SAMPLE_SIZE_THRESHOLD;

  const totals = useMemo(() => {
    const soi = summary?.rows.reduce((sum, r) => sum + (r.soi || 0), 0) ?? 0;
    const doi = summary?.rows.reduce((sum, r) => sum + (r.doi || 0), 0) ?? 0;
    const impressions = ga4Summary?.rows.reduce((sum, r) => sum + (r.impressions || 0), 0) ?? 0;
    const doiPerSoi = soi > 0 ? (doi / soi) * 100 : null;
    const doiPerImpression = impressions > 0 ? (doi / impressions) * 100 : null;
    return { impressions, soi, doi, doiPerSoi, doiPerImpression };
  }, [summary, ga4Summary]);

  const zTest = summary?.zTest ?? null;
  const ciExcludesZero =
    zTest !== null &&
    zTest.ci95Low !== null &&
    zTest.ci95High !== null &&
    (zTest.ci95Low > 0 || zTest.ci95High < 0);

  const declareWinner = sampleSizeOk && ciExcludesZero;

  const verdictText = (() => {
    if (!a1Row || !a2Row) return 'Beide Varianten benötigen mindestens eine Einreichung.';
    if (!sampleSizeOk) {
      const remaining = SAMPLE_SIZE_THRESHOLD - minDoiPerArm;
      return `Noch nicht aussagekräftig — es fehlen ${formatGerman.format(remaining)} DOIs pro Arm bis zur Mindeststichprobe (${SAMPLE_SIZE_THRESHOLD}).`;
    }
    if (!ciExcludesZero) {
      return 'Statistisch nicht signifikant — das 95 %-Konfidenzintervall enthält Null.';
    }
    const leader = (zTest?.diff ?? 0) > 0 ? 'A2' : 'A1';
    return `Signifikant: ${leader} führt mit ${formatPp(Math.abs(zTest?.diff ?? 0))}.`;
  })();

  const renderHeadline = (text: string): string =>
    substituteAktie ? text.replace(/\{AKTIE\}/g, PLACEHOLDER_SAMPLE) : text;

  const copyChangedDuringWindow = useMemo(() => {
    if (!queryParams) return [];
    const start = dayjs(queryParams.startDate).startOf('day');
    const end = dayjs(queryParams.endDate).endOf('day');
    return variantCopy.filter((c) => {
      if (!c.updated_at) return false;
      const ts = dayjs(c.updated_at);
      return ts.isAfter(start) && ts.isBefore(end);
    });
  }, [variantCopy, queryParams]);

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Lead Wall A/B Test
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Vergleicht die Copy-Varianten <strong>A1 „Sachlich“</strong> und{' '}
        <strong>A2 „Überzeuger“</strong> entlang des Funnels Impression → SOI → DOI.
      </Typography>

      {!sampleSizeOk && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stichprobe ist noch zu klein für eine belastbare Aussage. Mindestgröße:{' '}
          {SAMPLE_SIZE_THRESHOLD} DOIs pro Arm. Aktuell: A1 = {a1Row?.doi ?? 0}, A2 ={' '}
          {a2Row?.doi ?? 0}.
        </Alert>
      )}

      {copyChangedDuringWindow.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Achtung: Im aktuellen Zeitraum wurde der Copy-Text geändert (
          {copyChangedDuringWindow.map((c) => c.variant).join(', ')}). Die Funnel-Zahlen mischen
          also alte und neue Texte.
        </Alert>
      )}

      {/* Overall KPI strip — combined across both variants */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="overline" color="text.secondary" display="block" mb={1}>
          Gesamt — A1 + A2 kombiniert
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              label: 'Impressions (GA4)',
              value: formatGerman.format(totals.impressions),
              sub: 'Leadwall-Einblendungen · GA4 ab 24.04.',
              accent: colors.blueAccent[600],
            },
            {
              label: 'SOI (Submissions)',
              value: formatGerman.format(totals.soi),
              sub: 'E-Mail abgesendet',
              accent: colors.blueAccent[700],
            },
            {
              label: 'DOI (Leads)',
              value: formatGerman.format(totals.doi),
              sub: 'bestätigte Leads',
              accent: colors.greenAccent[700],
            },
            {
              label: 'DOI / SOI',
              value: formatPct(totals.doiPerSoi, 1),
              sub: 'Bestätigungsrate',
              accent: colors.greenAccent[600],
            },
            {
              label: 'DOI / Impression',
              value: formatPct(totals.doiPerImpression, 2),
              sub: 'Lead-Rate (gesamt)',
              accent: colors.greenAccent[500],
            },
          ].map((kpi) => (
            <Grid item xs={12} sm={6} md={2.4} key={kpi.label}>
              <Box sx={{ borderLeft: `4px solid ${kpi.accent}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {kpi.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {kpi.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {kpi.sub}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Combined volume chart — impressions (bars) + SOI/DOI (lines) */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="h6" mb={0.5}>
          Volumen-Verlauf — Impressions & Leads (gesamt)
        </Typography>
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          Impression-Daten sind ab dem <strong>24.04.2026</strong> verfügbar — an diesem Tag wurde
          das Tracking eingerichtet. Der A/B-Test läuft seit dem 21.04., die ersten drei Tage zeigen
          daher nur SOI/DOI. Ab jetzt ist der Funnel vollständig erfasst.
        </Alert>
        <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
          Linke Achse: tägliche Leadwall-Einblendungen. Rechte Achse: SOI/DOI.
        </Typography>
        <LeadwallOverallChart impressions={dailyImpressions} timeline={timeline} />
      </Paper>

      {/* Funnel KPI grid */}
      <Grid container spacing={2} mb={3}>
        {(['A1', 'A2'] as const).map((variant) => {
          const row = summary?.rows.find((r) => r.variant === variant);
          const ga4Row = ga4Summary?.rows.find((r) => r.variant === variant);
          const impressions = ga4Row?.impressions ?? 0;
          const doi = row?.doi ?? 0;
          const doiPerImpression = impressions > 0 ? (doi / impressions) * 100 : null;
          const accent = variant === 'A1' ? colors.blueAccent[700] : colors.greenAccent[700];
          return (
            <Grid item xs={12} md={6} key={variant}>
              <Paper sx={{ p: 2, borderLeft: `5px solid ${accent}`, borderRadius: '10px' }}>
                <Typography variant="overline" color="text.secondary">
                  Variante {variant} {variant === 'A1' ? '· „Sachlich“' : '· „Überzeuger“'}
                </Typography>
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={6} md={2.4}>
                    <Typography variant="caption" color="text.secondary">
                      Impressions (GA4)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatGerman.format(impressions)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Typography variant="caption" color="text.secondary">
                      SOI
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatGerman.format(row?.soi ?? 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Typography variant="caption" color="text.secondary">
                      DOI
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatGerman.format(doi)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Typography variant="caption" color="text.secondary">
                      DOI / SOI
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatPct(row?.doi_pct ?? null)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Typography variant="caption" color="text.secondary">
                      DOI / Impr.
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatPct(doiPerImpression, 2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Significance cards */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            title: 'DOI / SOI — A1 vs A2',
            sub: 'Konversion ab dem Klick auf „Freischalten“. Saubere Rate, aber SOI enthält nur User, die ohnehin schon geklickt haben.',
            zt: zTest,
            decisive: declareWinner,
            verdict: verdictText,
          },
          {
            title: 'DOI / Impression — A1 vs A2 (Hauptmetrik)',
            sub: 'Konversion ab Leadwall-Einblendung. Berücksichtigt auch User, die ohne Klick weiterscrollen — bevorzugte Metrik für den Variantenvergleich.',
            zt: ga4Summary?.zTestDoiPerImpression ?? null,
            decisive:
              sampleSizeOk &&
              !!ga4Summary?.zTestDoiPerImpression &&
              ga4Summary.zTestDoiPerImpression.ci95Low !== null &&
              ga4Summary.zTestDoiPerImpression.ci95High !== null &&
              (ga4Summary.zTestDoiPerImpression.ci95Low > 0 ||
                ga4Summary.zTestDoiPerImpression.ci95High < 0),
            verdict: (() => {
              const z = ga4Summary?.zTestDoiPerImpression;
              if (!z) return 'Daten werden geladen…';
              if (!sampleSizeOk) {
                return `Noch nicht aussagekräftig — Stichprobe zu klein (≥${SAMPLE_SIZE_THRESHOLD} DOIs / Arm benötigt).`;
              }
              const exclZero =
                z.ci95Low !== null && z.ci95High !== null && (z.ci95Low > 0 || z.ci95High < 0);
              if (!exclZero)
                return 'Statistisch nicht signifikant — Konfidenzintervall enthält Null.';
              const leader = z.diff > 0 ? 'A2' : 'A1';
              return `Signifikant: ${leader} führt mit ${formatPp(Math.abs(z.diff))}.`;
            })(),
          },
        ].map((card, idx) => (
          <Grid item xs={12} md={6} key={idx}>
            <Paper sx={{ p: 2, borderRadius: '10px', height: '100%' }}>
              <Typography variant="h6" mb={0.5}>
                {card.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {card.sub}
              </Typography>
              <Typography
                variant="body1"
                color={card.decisive ? colors.greenAccent[600] : 'text.secondary'}
                fontWeight={card.decisive ? 'bold' : 'normal'}
                mb={1.5}
              >
                {card.verdict}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Differenz (A2 − A1)
                  </Typography>
                  <Typography variant="h6">{formatPp(card.zt?.diff ?? null)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    95 %-Konfidenzintervall
                  </Typography>
                  <Typography variant="h6">
                    {card.zt?.ci95Low !== null && card.zt?.ci95Low !== undefined
                      ? `[${formatPp(card.zt.ci95Low)}, ${formatPp(card.zt.ci95High ?? null)}]`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    p-Wert
                  </Typography>
                  <Typography variant="h6">{formatPValue(card.zt?.pValue ?? null)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Benötigt pro Arm
                  </Typography>
                  <Typography variant="h6">
                    {card.zt?.nRequiredPerArm ? formatGerman.format(card.zt.nRequiredPerArm) : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" mb={3}>
        Zwei-Stichproben-Z-Test auf Anteilen, ungepoolte SE für das Konfidenzintervall. Benötigte
        Stichprobe berechnet für 80 % Power, α = 0,05, MDE = 2 pp absolut um die beobachtete Rate.
        „Noch nicht aussagekräftig“ solange min(DOI A1, DOI A2) {'<'} {SAMPLE_SIZE_THRESHOLD}.
      </Typography>

      {/* Variant copy panel */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Aktueller Copy-Stand</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={substituteAktie}
                onChange={(e) => setSubstituteAktie(e.target.checked)}
                size="small"
              />
            }
            label={`Platzhalter ersetzen (${PLACEHOLDER_SAMPLE})`}
          />
        </Box>
        <Grid container spacing={2}>
          {variantCopy.map((c) => (
            <Grid item xs={12} md={6} key={c.variant}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Chip
                    label={c.variant}
                    size="small"
                    color={c.variant.startsWith('A1') ? 'primary' : 'success'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    geändert: {c.updated_at}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  {renderHeadline(c.headline)}
                </Typography>
                <Typography variant="body2" mb={1}>
                  {renderHeadline(c.intro)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {c.benefits_header}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {c.benefits.map((b, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">{renderHeadline(b)}</Typography>
                    </li>
                  ))}
                </ul>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  → {c.button}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.trust}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Daily timeseries */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="h6" mb={1}>
          Täglicher Verlauf — SOI & DOI je Variante
        </Typography>
        <LeadwallTimelineChart data={timeline} />
      </Paper>

      {/* Article count breakdown (GA4) */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="h6" mb={0.5}>
          Leadwall-Einblendungen nach Artikel-Aufruf (GA4)
        </Typography>
        <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
          Beim wievielten Artikel-Aufruf wurde die Leadwall ausgelöst? 4 ist die Mindestschwelle,
          höhere Werte zeigen wiederholte Einblendungen bei Vielleser:innen. Der Rückgang ist
          normal.
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                >
                  Aufruf-Nr.
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                >
                  A1 Impr.
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                >
                  A2 Impr.
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                >
                  Σ
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const grouped = new Map<number, { a1: number; a2: number }>();
                for (const r of articleCount) {
                  const entry = grouped.get(r.article_count) ?? { a1: 0, a2: 0 };
                  if (r.variant === 'A1') entry.a1 += r.impressions;
                  else if (r.variant === 'A2') entry.a2 += r.impressions;
                  grouped.set(r.article_count, entry);
                }
                const ordered = Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
                if (ordered.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Keine GA4-Daten im Zeitraum.
                      </TableCell>
                    </TableRow>
                  );
                }
                return ordered.map(([articleIndex, val]) => (
                  <TableRow key={articleIndex}>
                    <TableCell>{articleIndex}</TableCell>
                    <TableCell align="right">{formatGerman.format(val.a1)}</TableCell>
                    <TableCell align="right">{formatGerman.format(val.a2)}</TableCell>
                    <TableCell align="right">{formatGerman.format(val.a1 + val.a2)}</TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Top articles table */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="h6" mb={1}>
          Top-Artikel nach DOI-Anzahl
        </Typography>
        <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
          Klick auf eine Zeile öffnet den Artikel in einem neuen Tab.
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Artikel
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  A1 SOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  A1 DOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  A2 SOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  A2 DOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Σ SOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Σ DOI
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  DOI %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topArticles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Keine Artikel-Konversionen im Zeitraum.
                  </TableCell>
                </TableRow>
              ) : (
                topArticles.map((row) => (
                  <TableRow
                    key={row.article_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => window.open(articleUrl(row), '_blank')}
                  >
                    <TableCell>{row.post_title}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.variant_a1_soi)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.variant_a1_doi)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.variant_a2_soi)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.variant_a2_doi)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.soi)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.doi)}</TableCell>
                    <TableCell align="right">{formatPct(row.doi_pct)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* LPs cross-check */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Typography variant="h6" mb={1}>
          Funnel-Konsistenz (LPs-Abgleich)
        </Typography>
        <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
          Vergleicht die Lead Wall-Einträge mit dem zentralen LPs-System. Abweichungen deuten auf
          abgebrochene oder fehlerhafte Funnel-Übergaben hin.
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Variante
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Σ Einträge
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  DOI bestätigt
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Unbestätigt
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  Fehlgeschlagen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lpsCrossCheck.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Keine Datensätze im Zeitraum.
                  </TableCell>
                </TableRow>
              ) : (
                lpsCrossCheck.map((row) => (
                  <TableRow key={row.variant}>
                    <TableCell>{row.variant}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.rows_total)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.doi_confirmed)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.unconfirmed)}</TableCell>
                    <TableCell align="right">{formatGerman.format(row.errored)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {loading && (
        <Typography variant="caption" color="text.secondary">
          Lade Daten…
        </Typography>
      )}
    </Box>
  );
};

export default LeadwallEvaluationPage;
